// Single entry point for talking to the SmartClaim API.
//
// Everything that calls the backend goes through apiFetch so session expiry is
// handled in one place: `fetchFromApi` never checked response status, and the
// layout only authenticates on mount, so an expired session used to leave the
// UI quietly broken rather than sending anyone back to the login screen.

import { get } from "svelte/store";
import { goto } from "$app/navigation";
import { authenticated_user, current_user } from "./store";

export const BASE_URL = import.meta.env.VITE_BASE_URL || "http://localhost:8083";

// Endpoints where a 401 means "those credentials are wrong", not "your session
// ended" - a wrong password or a wrong 6-digit code must show its own error
// rather than bouncing the user to a login screen they are already looking at.
const CREDENTIAL_PATHS = [
    /^\/login\b/,
    /^\/register\b/,
    /^\/change-password/,
    /^\/verify\b/,
    // Only the steps that check a factor. The management endpoints below them
    // (/totp/status, /totp/credentials, /webauthn/credentials) do run on a
    // session, so a 401 there really does mean the session has gone.
    /^\/totp\/(authenticate|register)\//,
    /^\/webauthn\/(authenticate|register)\//,
    // The session check itself: the layout handles a failed /me (public pages
    // just stay logged out, protected ones redirect with ?next=).
    /^\/me$/,
];

export class SessionExpiredError extends Error {
    constructor() {
        super("Your session has expired, please log in again");
        this.name = "SessionExpiredError";
        this.status = 401;
    }
}

function isCredentialEndpoint(path) {
    return CREDENTIAL_PATHS.some((re) => re.test(path));
}

// Modules with their own teardown (the SSE stream, for one) register here at
// import time. Inverted deliberately: this module knowing about theirs would
// make a cycle, since they call apiFetch.
const cleanupHandlers = [];

export function onSessionExpired(handler) {
    cleanupHandlers.push(handler);
}

// One redirect no matter how many requests fail at once - an expired session
// typically 401s every in-flight call on the page simultaneously.
let expiring = false;

// Logging in again doesn't reload the page, so re-arm for the next expiry
// in this tab once a user is back.
current_user.subscribe((user) => {
    if (user) expiring = false;
});

function handleSessionExpired() {
    if (expiring) return;
    expiring = true;

    authenticated_user.set(null);
    current_user.set(null);

    for (const handler of cleanupHandlers) {
        try {
            handler();
        } catch (e) {
            console.warn("Session cleanup handler failed:", e);
        }
    }

    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        goto("/login?expired=1");
    }
}

/**
 * fetch against the API, with session handling attached.
 * Returns the raw Response - callers decide how to read it.
 * Throws SessionExpiredError when the session is gone.
 */
export async function apiFetch(path, options = {}) {
    const res = await fetch(`${BASE_URL}${path}`, { credentials: "include", ...options });

    if (res.status === 401 && !isCredentialEndpoint(path)) {
        handleSessionExpired();
        throw new SessionExpiredError();
    }

    return res;
}

/**
 * For failures that can't show a 401 themselves: EventSource never reports
 * the status of a refused connection, so an expired session looks just like
 * a network drop. Asks /me, and when the session has gone, sends the user to
 * the login screen. Resolves true in that case.
 */
export async function endSessionIfExpired() {
    try {
        const res = await apiFetch("/me");
        if (res.status !== 401) return false;
    } catch {
        // Offline or the API is down: a connection problem, not the session.
        return false;
    }
    handleSessionExpired();
    return true;
}

/** True while a session is present in the stores. */
export function hasSession() {
    return Boolean(get(authenticated_user) || get(current_user));
}
